// chatbotData.js - Comprehensive HR Knowledge Base with Fixed Exports
export const hrKnowledge = {
  greetings: [
    "Hello! I'm HR Copilot 🤖 How can I assist you with HR-related questions today?",
    "Hi there! Ready to help with your HR queries. What do you need assistance with?",
    "Welcome to HR Copilot! I'm here to help with onboarding, leave, benefits, and more.",
    "Greetings! I'm your AI HR assistant. How can I make your HR experience better today?"
  ],
  
  onboarding: {
    process: [
      "The onboarding process takes 3-5 business days. Steps include: document submission, account setup, system training, and team introduction.",
      "New hires complete: employment contract, tax forms, bank details, and equipment setup. All done electronically through our portal.",
      "Onboarding workflow: 1. Pre-arrival paperwork 2. First day orientation 3. System access setup 4. Team integration 5. 30-day check-in"
    ],
    documents: [
      "Required documents: Government ID, Social Security card, educational certificates, and bank details for payroll.",
      "You'll need to submit: Identity proof, address proof, and bank account information digitally.",
      "Mandatory documents: W-4 form, I-9 verification, direct deposit form, emergency contact info, and benefits enrollment forms."
    ],
    timeline: [
      "Day 1: Paperwork & system access\nDay 2: Training sessions\nDay 3: Team introduction\nDay 4-5: Role-specific training",
      "Onboarding completion typically takes 3-5 days with all mandatory trainings and setup.",
      "Week 1: Orientation and system training\nWeek 2: Role-specific onboarding\nMonth 1: Full integration and first review"
    ],
    checklist: [
      "Onboarding checklist: Complete paperwork, set up email/accounts, attend orientation, meet team, review policies, set goals.",
      "Essential steps: Submit all forms, complete compliance training, set up workstation, understand company culture."
    ]
  },

  leave: {
    types: [
      "We offer: 20 days annual leave, 10 sick days, 5 personal days, and maternity/paternity leave as per policy.",
      "Leave types: Vacation, sick leave, personal time, parental leave, and bereavement leave.",
      "Available leave: Annual (20 days), Sick (10 days), Personal (5 days), Parental (12 weeks), Bereavement (3 days)"
    ],
    process: [
      "Request leave through Employee Portal → Time & Attendance → Leave Request. Approval in 24-48 hours.",
      "Submit leave requests via the portal. Managers receive notifications for immediate approval.",
      "Leave request steps: 1. Login to portal 2. Go to Time & Attendance 3. Select dates 4. Submit for approval"
    ],
    balance: [
      "Check leave balance: Login to portal → Dashboard → Leave Balance (updated in real-time).",
      "Your remaining leave days are visible in the Time & Attendance section of your dashboard.",
      "Leave balances update automatically after each approved request. View history in your profile."
    ],
    approval: [
      "Leave approval: Managers review within 24 hours. Emergency leave requests get priority processing.",
      "Approval workflow: Submit request → Manager review → HR verification → Confirmation email"
    ]
  },

  benefits: {
    healthcare: [
      "Healthcare: Medical, dental, vision insurance with 80% company contribution. Coverage starts after 30 days.",
      "We offer comprehensive health insurance including medical, dental, and vision coverage.",
      "Health benefits: PPO and HMO options, prescription coverage, mental health services, wellness programs."
    ],
    retirement: [
      "Retirement: 401(k) with 4% company match. Vesting period: 3 years.",
      "Retirement benefits include 401(k) matching and financial planning resources.",
      "401(k) details: Immediate eligibility, various investment options, Roth 401(k) available, annual rebalancing."
    ],
    other: [
      "Additional benefits: Wellness program, professional development fund, flexible spending accounts.",
      "Other benefits include tuition reimbursement, gym membership, and employee assistance program.",
      "Extra perks: Commuter benefits, employee discounts, referral bonuses, professional certification support."
    ],
    enrollment: [
      "Benefits enrollment: Open enrollment each November. New hires enroll within 30 days of start date.",
      "To enroll: Access benefits portal, compare plans, select options, submit by deadline."
    ]
  },

  payroll: {
    schedule: [
      "Payroll runs bi-weekly. Direct deposit every other Friday. Cut-off: Wednesday 5 PM.",
      "Pay periods are bi-weekly. Payday is every other Friday via direct deposit.",
      "Pay schedule: 1st-15th and 16th-end of month. Payday: Following Friday after period end."
    ],
    documents: [
      "Access pay stubs: Portal → Payroll → Pay Stubs. Tax forms available in January.",
      "All payroll documents including W-2s and pay stubs are available in the payroll section.",
      "Digital documents: Current pay stubs, year-to-date earnings, tax documents, bonus statements."
    ],
    issues: [
      "Payroll issues? Contact payroll@hrcopilot.com or call extension 1234. Response within 24 hours.",
      "For payroll corrections, email payroll team with your employee ID and issue details.",
      "Common issues: Missing hours, incorrect deductions, bank account changes. Submit ticket for resolution."
    ],
    taxes: [
      "Tax forms: W-4 for federal, state withholding forms. Update anytime through payroll portal.",
      "Tax documents: W-2 by Jan 31st, 1095-C for health coverage, state-specific forms as required."
    ]
  },

  performance: {
    reviews: [
      "Performance reviews: Quarterly check-ins + annual comprehensive review. Next cycle starts April 1st.",
      "We conduct formal performance reviews quarterly with continuous feedback throughout the year.",
      "Review schedule: Q1 check-in (April), Q2 (July), Q3 (October), Annual review (January)."
    ],
    goals: [
      "Set and track goals in the Performance module. SMART goals recommended for better tracking.",
      "Goals should be Specific, Measurable, Achievable, Relevant, and Time-bound (SMART).",
      "Goal setting: Align with company objectives, include development goals, set milestones, regular updates."
    ],
    feedback: [
      "Request feedback anytime through the portal. 360-degree feedback available for managers.",
      "Continuous feedback is encouraged. Use the feedback tool for quick peer recognition.",
      "Feedback channels: Manager 1:1s, peer feedback, self-assessment, project retrospectives."
    ],
    development: [
      "Development plans: Created during annual reviews. Include training, mentorship, and growth opportunities.",
      "Career growth: Internal mobility, skill development programs, leadership training, cross-functional projects."
    ]
  },

  policies: {
    general: [
      "All policies available in Employee Handbook on portal. Key policies: Code of Conduct, Data Security, Remote Work.",
      "Company policies cover: attendance, conduct, confidentiality, and workplace safety.",
      "Essential policies: Anti-harassment, social media, expense reimbursement, travel, equipment usage."
    ],
    remote: [
      "Remote work policy: Hybrid model - 3 days office, 2 days remote. Equipment provided for remote work.",
      "Remote employees receive: Laptop, monitor, internet stipend, and ergonomic equipment.",
      "Remote guidelines: Core collaboration hours, communication expectations, home office setup requirements."
    ],
    attendance: [
      "Standard hours: 9 AM - 6 PM with 1-hour lunch. Flexible timing available with manager approval.",
      "Core hours: 10 AM - 4 PM. Flexible start times between 7-10 AM.",
      "Attendance tracking: Portal check-in/out, project time logging, meeting punctuality expectations."
    ],
    code: [
      "Code of Conduct: Professional behavior, respect for all, confidentiality, conflict of interest disclosure.",
      "Ethical standards: No discrimination, proper use of assets, accurate reporting, compliance with laws."
    ]
  },

  training: {
    mandatory: [
      "Required training: Safety, anti-harassment, data security, compliance. Complete within first 30 days.",
      "Mandatory courses: Workplace safety, diversity & inclusion, cybersecurity, code of conduct."
    ],
    development: [
      "Development programs: Leadership training, technical skills, soft skills, certification support.",
      "Available training: Management development, communication skills, project management, industry certifications."
    ],
    platform: [
      "Access training: Learning Management System (LMS) → Courses → Enroll. Track progress and completion.",
      "LMS features: Self-paced courses, live workshops, skill assessments, completion certificates."
    ]
  },

  compliance: {
    regulations: [
      "We comply with: EEOC, ADA, FMLA, OSHA, and state-specific employment laws.",
      "Legal compliance: Equal opportunity, reasonable accommodation, family leave, workplace safety standards."
    ],
    reporting: [
      "Report concerns: Anonymous hotline, direct to manager, HR department, or compliance officer.",
      "Reporting channels: Ethics hotline, HR contact, manager escalation, compliance team email."
    ],
    documentation: [
      "Required records: I-9 verification, background checks, training completion, performance documentation.",
      "Compliance documents: Policy acknowledgments, safety training records, harassment prevention certification."
    ]
  },

  technology: {
    systems: [
      "HR systems: Employee portal, payroll platform, benefits administration, performance management.",
      "Available tools: HRIS for data, LMS for training, collaboration platforms, time tracking software."
    ],
    access: [
      "System access: Provided during onboarding. Reset passwords through IT help desk or self-service portal.",
      "Technical support: IT help desk Mon-Fri 8AM-6PM, emergency support for critical system issues."
    ],
    security: [
      "Security protocols: Multi-factor authentication, regular password changes, encrypted data transmission.",
      "Data protection: Secure login, role-based access, regular security training, incident response plan."
    ]
  },

  culture: {
    values: [
      "Our values: Integrity, collaboration, innovation, customer focus, and employee development.",
      "Company culture: Supportive environment, continuous learning, work-life balance, recognition programs."
    ],
    events: [
      "Regular events: Team building, holiday parties, volunteer days, professional development workshops.",
      "Cultural activities: Monthly socials, quarterly all-hands, annual retreat, department outings."
    ],
    recognition: [
      "Recognition programs: Employee of the month, peer recognition, service awards, spot bonuses.",
      "Appreciation: Public recognition, monetary awards, extra time off, professional development opportunities."
    ]
  },

  career: {
    growth: [
      "Career paths: Individual contributor and management tracks. Discuss with manager during reviews.",
      "Growth opportunities: Promotions, lateral moves, special projects, mentorship programs."
    ],
    internal: [
      "Internal mobility: Postings on internal portal. Employees can apply after 12 months in current role.",
      "Internal jobs: Posted weekly. Priority consideration for internal candidates before external posting."
    ],
    development: [
      "Professional development: Tuition reimbursement, certification support, conference attendance, skill workshops.",
      "Learning resources: Online courses, book allowances, coaching, cross-training opportunities."
    ]
  },

  facilities: {
    office: [
      "Office amenities: Cafeteria, gym, meditation rooms, collaboration spaces, parking facilities.",
      "Workplace features: Ergonomic furniture, natural lighting, quiet zones, meeting rooms, social areas."
    ],
    equipment: [
      "Provided equipment: Laptop, monitor, phone, headset, necessary software licenses.",
      "Equipment policy: Regular upgrades, repair/replacement process, personal device usage guidelines."
    ],
    safety: [
      "Safety protocols: Emergency evacuation plans, first aid kits, security personnel, incident reporting.",
      "Workplace safety: Regular drills, safety training, hazard reporting, emergency contact information."
    ]
  },

  communication: {
    channels: [
      "Communication tools: Email, instant messaging, video conferencing, project management platforms.",
      "Official channels: Company newsletter, all-hands meetings, department updates, portal announcements."
    ],
    meetings: [
      "Regular meetings: Weekly team meetings, monthly department reviews, quarterly company updates.",
      "Meeting culture: Agenda required, time respect, action items, follow-up documentation."
    ],
    feedback: [
      "Employee feedback: Annual surveys, suggestion box, town halls, manager 1:1s, pulse surveys.",
      "Voice programs: Employee resource groups, advisory committees, innovation challenges."
    ]
  },

  default: [
    "I'm not sure about that specific question. Could you rephrase or ask about: onboarding, leave, benefits, payroll, performance, or policies?",
    "That's an interesting question! For detailed assistance, please contact HR support at support@hrcopilot.com",
    "I specialize in common HR queries. For complex issues, our HR team is available at hr@company.com",
    "I'd be happy to help! Could you provide more details or ask about a specific HR topic?",
    "Let me connect you with the right information. What specific area of HR are you interested in?"
  ]
};

// Extended quick questions for common HR queries
export const quickQuestions = [
  "How to request leave?",
  "What are the onboarding steps?",
  "How to check my benefits?",
  "When is payday?",
  "How to set performance goals?",
  "Where to find company policies?",
  "What training is required?",
  "How to update my tax forms?",
  "What's the remote work policy?",
  "How to report an issue?",
  "Career growth opportunities?",
  "How to access HR systems?",
  "What's the dress code?",
  "How to request equipment?",
  "Team building activities?",
  "Health insurance details?",
  "Retirement plan options?",
  "Professional development?",
  "Time off balance?",
  "Performance review schedule?"
];

// Suggested Topics for Chatbot
export const suggestedTopics = [
  {
    icon: '🏖️',
    title: 'Leave Policy',
    description: 'Annual, sick, and parental leave details',
    question: 'What are the different types of leave available?'
  },
  {
    icon: '💰',
    title: 'Payroll Schedule',
    description: 'Payment dates and direct deposit info',
    question: 'When is the next payday?'
  },
  {
    icon: '🏥',
    title: 'Health Benefits',
    description: 'Insurance plans and coverage details',
    question: 'What health benefits are available?'
  },
  {
    icon: '🏠',
    title: 'Remote Work',
    description: 'Work from home policies',
    question: 'What is the remote work policy?'
  },
  {
    icon: '🚀',
    title: 'Onboarding',
    description: 'New hire process and timeline',
    question: 'What is the onboarding process?'
  },
  {
    icon: '📊',
    title: 'Performance Reviews',
    description: 'Review cycles and process',
    question: 'How do performance reviews work?'
  }
];

// Additional HR resources
export const hrResources = {
  contacts: {
    hr: "HR Department: hr@company.com | Ext: 1001",
    payroll: "Payroll: payroll@company.com | Ext: 1002",
    benefits: "Benefits: benefits@company.com | Ext: 1003",
    it: "IT Support: helpdesk@company.com | Ext: 2000",
    facilities: "Facilities: facilities@company.com | Ext: 3000"
  },
  
  deadlines: {
    benefits: "Open Enrollment: November 1-30 annually",
    performance: "Review Cycles: Q1 Apr, Q2 Jul, Q3 Oct, Annual Jan",
    taxes: "W-4 Updates: Anytime, W-2 Distribution: Jan 31st",
    timeoff: "Leave Requests: Submit at least 2 weeks in advance"
  },
  
  emergency: {
    contacts: "Emergency: Security: Ext: 911, Medical: Ext: 912",
    procedures: "Evacuation: Follow exit signs, Assembly: Parking lot B",
    reporting: "Incidents: Report immediately to manager and HR"
  }
};

// Common HR scenarios and responses
export const commonScenarios = {
  newHire: [
    "Welcome! Complete onboarding paperwork, attend orientation, and set up your workstation.",
    "As a new hire: Submit documents, complete training, meet your team, and understand company policies."
  ],
  
  promotion: [
    "Congratulations! Update your profile, review new responsibilities, and schedule transition meetings.",
    "For promotions: Discuss new goals, understand changed benefits, and update your development plan."
  ],
  
  resignation: [
    "Submit formal resignation letter, complete exit interview, return company property, and transition work.",
    "Resignation process: Provide notice, knowledge transfer, exit formalities, and final paycheck details."
  ],
  
  complaint: [
    "Report concerns to your manager, HR, or use the anonymous hotline. All reports are confidential.",
    "For complaints: Document details, follow reporting procedure, and expect timely investigation."
  ]
};

// Extended questions for more variety
export const extendedQuestions = [
  'How do I apply for leave?',
  'What documents are needed for onboarding?',
  'How to check my leave balance?',
  'What is the retirement plan?',
  'How to update personal information?',
  'What is the dress code policy?',
  'How to request equipment?',
  'What are the working hours?',
  'How to submit expense reports?',
  'What training programs are available?',
  'How to report an issue?',
  'What is the bonus structure?',
  'How does promotion work?',
  'What wellness programs are offered?',
  'How to access pay stubs?'
];

export default {
  hrKnowledge,
  quickQuestions,
  suggestedTopics,
  hrResources,
  commonScenarios,
  extendedQuestions
};